import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { flushSync } from 'react-dom';
import { Button, Toast, ToastContainer, Dropdown } from 'react-bootstrap';
import { chatApi } from '../services/api';
import useSettings from '../hooks/useSettings';
import useEmbedCommunication from '../hooks/useEmbedCommunication';
import WelcomeComponent from './WelcomeComponent';
import ConversationStarters from './ConversationStarters';
import ReactMarkdown from 'react-markdown';
import './ChatInterface.css';
import './iframe-styles.css';

// Custom link renderer for ReactMarkdown - opens links in new tab
const markdownComponents = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="markdown-link">
      {children}
      <i className="bi bi-box-arrow-up-right external-link-icon"></i>
    </a>
  )
};

const TOOL_STATUS = {
  search_knowledge_base: { icon: 'bi-journal-text', label: 'Searching knowledge base...' },
  search_web: { icon: 'bi-globe', label: 'Searching the web...' },
};

const COMPOSER_MAX_HEIGHT = 200;

// Morph the composer from the welcome screen to the docked position when supported
const runWithViewTransition = (update) => {
  const canAnimate = typeof document.startViewTransition === 'function'
    && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (!canAnimate) {
    update();
    return;
  }
  document.startViewTransition(() => flushSync(update));
};

const ChatInterface = () => {
  const MAX_MESSAGE_LENGTH = 2000;
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatStarters, setChatStarters] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [expandedCitations, setExpandedCitations] = useState({});
  
  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [activeToolCall, setActiveToolCall] = useState(null);
  const abortStreamRef = useRef(null);
  
  // Reference to the input field for focusing
  const inputRef = useRef(null);

  // Reference to the scrollable chat window for auto-scroll
  const chatWindowRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  
  // Embed communication: postMessage handshake for auto-resize + parent URL capture
  const { isEmbedded, parentUrl } = useEmbedCommunication();

  // Apply in-iframe class for CSS adjustments when embedded
  useEffect(() => {
    if (isEmbedded) {
      document.documentElement.classList.add('in-iframe');
    }
    
    return () => {
      document.documentElement.classList.remove('in-iframe');
    };
  }, [isEmbedded]);

  // Keep following new content unless the reader has scrolled up.
  // Scrolls only the chat window so the host page never jumps when embedded.
  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    if (!chatWindow) return;
    if (messages[messages.length - 1]?.role === 'user') stickToBottomRef.current = true;
    if (!stickToBottomRef.current) return;
    chatWindow.scrollTo?.({ top: chatWindow.scrollHeight, behavior: streamingText ? 'auto' : 'smooth' });
  }, [messages, streamingText, activeToolCall]);

  // Only an upward scroll releases the stick, so smooth auto-scrolling never cancels itself
  const handleChatScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 80;
    if (nearBottom) stickToBottomRef.current = true;
    else if (scrollTop < lastScrollTopRef.current) stickToBottomRef.current = false;
    lastScrollTopRef.current = scrollTop;
  };

  // Use settings hook to get chat starters
  const { getSetting, isLoading: settingsLoading, isLoaded } = useSettings();
  
  // Update chat starters when settings are loaded
  useEffect(() => {
    // Only update when settings are loaded
    if (isLoaded) {
      // Get chat starters from settings
      const startersString = getSetting('chat_starters', '');
      const startersArray = startersString ? startersString.split(';').filter(Boolean) : [];
      setChatStarters(startersArray);
    }
    
    // Update loading state
    // setLoadingStarters(settingsLoading);
  }, [isLoaded]); // Only depend on isLoaded to prevent loops

  const isWelcome = messages.length === 0;
  const isNearLimit = inputMessage.length > MAX_MESSAGE_LENGTH * 0.9;
  const canSend = Boolean(inputMessage.trim()) && inputMessage.length <= MAX_MESSAGE_LENGTH && !isLoading;

  // Grow the composer with its content up to a maximum height
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, [inputMessage, isWelcome]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) {
        handleSubmit(e);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    if (inputMessage.length > MAX_MESSAGE_LENGTH) {
      setToastMessage(`Message exceeds maximum length of ${MAX_MESSAGE_LENGTH} characters`);
      setShowToast(true);
      return;
    }
    
    // Add user message to chat
    const userMessage = { role: 'user', content: inputMessage };
    const currentMessage = inputMessage.trim();
    const historySnapshot = messages;
    const commitUserMessage = () => {
      setMessages(prevMessages => [...prevMessages, userMessage]);
      setInputMessage('');
      setIsLoading(true);
      setIsStreaming(true);
      setStreamingText('');
      setActiveToolCall(null);
    };
    if (isWelcome) {
      runWithViewTransition(commitUserMessage);
    } else {
      commitUserMessage();
    }
    
    let accumulatedText = '';
    let streamStarted = false; // Track if stream started (user message already saved on backend)
    
    // Try streaming first, fallback to non-streaming on error
    try {
      abortStreamRef.current = chatApi.sendMessageStream(
        currentMessage,
        conversationId,
        parentUrl,
        {
          onConversationId: (id) => {
            setConversationId(id);
            streamStarted = true; // conversation_id received = user message already persisted
          },
          onText: (text) => {
            accumulatedText += text;
            setStreamingText(accumulatedText);
            setActiveToolCall(null); // Clear tool call indicator when text starts
          },
          onToolCallStarted: (name) => {
            setActiveToolCall(name);
          },
          onToolResult: (name, citations) => {
            // Don't accumulate here - wait for done event with complete deduplicated list
            setActiveToolCall(null);
          },
          onGenerating: () => {
            setActiveToolCall(null);
          },
          onDone: (citations) => {
            // Use citations from done event (already complete and deduplicated from backend)
            const finalCitations = citations || [];
            
            // Add the complete assistant message
            setMessages(prevMessages => [
              ...prevMessages,
              {
                role: 'assistant',
                content: accumulatedText,
                hasCitations: finalCitations.length > 0,
                citations: finalCitations
              }
            ]);
            
            setIsLoading(false);
            setIsStreaming(false);
            setStreamingText('');
            setActiveToolCall(null);
            abortStreamRef.current = null;
          },
          onError: async (error) => {
            if (streamStarted) {
              // Stream already started — user message was already saved to DB.
              // Do NOT fall back to non-streaming or the user message will be logged twice.
              console.error('Streaming error after stream started:', error);
              setMessages(prevMessages => [
                ...prevMessages,
                {
                  role: 'assistant',
                  content: 'Sorry, I encountered an error while generating a response. Please try again.',
                  citations: []
                }
              ]);
            } else {
              // Stream never started — user message was NOT saved. Safe to fall back.
              console.error('Streaming error, falling back to non-streaming:', error);
              
              try {
                const result = await chatApi.sendMessage(currentMessage, conversationId);
                
                if (result.conversation_id) {
                  setConversationId(result.conversation_id);
                }
                
                setMessages(prevMessages => [
                  ...prevMessages,
                  {
                    role: 'assistant',
                    content: result.text,
                    hasCitations: result.citations && result.citations.length > 0,
                    citations: result.citations || []
                  }
                ]);
              } catch (fallbackError) {
                console.error('Fallback also failed:', fallbackError);
                setMessages(prevMessages => [
                  ...prevMessages,
                  {
                    role: 'assistant',
                    content: 'Sorry, I encountered an error. Please try again later.',
                    citations: []
                  }
                ]);
              }
            }
            
            setIsLoading(false);
            setIsStreaming(false);
            setStreamingText('');
            setActiveToolCall(null);
            abortStreamRef.current = null;
          }
        },
        historySnapshot
      );
    } catch (error) {
      console.error('Error initiating stream:', error);
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingText('');
      setActiveToolCall(null);
    }
  };

  // Place the starter in the composer so the user can edit it before sending
  const handleStarterClick = (starter) => {
    const text = starter.trim().slice(0, MAX_MESSAGE_LENGTH);
    flushSync(() => setInputMessage(text));
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(text.length, text.length);
  };
  
  // Handle exporting a message with specified format
  const handleExportMessage = async (message, index, format = 'pdf') => {
    try {
      setExportLoading(true);
      
      // Generate a unique message ID if not available
      const messageId = `msg-${Date.now()}-${index}`;
      
      // Update the API service to include the message content, citations, and format
      const result = await chatApi.exportMessageAsPdf(
        message.content,
        messageId,
        conversationId,
        message.citations || [],
        format
      );
      
      // Show success toast with appropriate format
      const formatName = format.toUpperCase();
      setToastMessage(`Message exported as ${formatName} successfully!`);
      setShowToast(true);
    } catch (error) {
      console.error(`Error exporting message as ${format}:`, error);
      setToastMessage(`Failed to export message as ${format}. Please try again.`);
      setShowToast(true);
    } finally {
      setExportLoading(false);
    }
  };
  
  // Handle copying message content to clipboard with citations
  const handleCopyMessage = async (message) => {
    try {
      
      // Start with the message content
      let textToCopy = message.content;
      
      // Add citations if they exist
      if (message.hasCitations && message.citations && message.citations.length > 0) {
        textToCopy += '\n\n## Citations\n';
        message.citations.forEach((citation, index) => {
          const citationLabel = citation.type === 'web' && citation.id ? `[${citation.id}]` : `${index + 1}.`;
          textToCopy += `\n${citationLabel} ${citation.filename}`;
          if (citation.url && citation.url !== citation.filename) {
            textToCopy += ` - ${citation.url}`;
          }
        });
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setToastMessage('Message with citations copied to clipboard!');
      setShowToast(true);
    } catch (error) {
      console.error('Error copying message:', error);
      setToastMessage('Failed to copy message. Please try again.');
      setShowToast(true);
    }
  };
  

  const avatar = (
    <img src="img/karl-b-gpt.png" alt="" aria-hidden="true" className="avatar-image" />
  );
  const toolStatus = TOOL_STATUS[activeToolCall] || { icon: 'bi-gear', label: `Running ${activeToolCall}...` };

  // The chat window, composer, and starters keep fixed positions in this tree so the
  // composer is never remounted (focus and draft survive the welcome-to-chat switch).
  return (
    <div className={`chat-container ${isWelcome ? 'is-welcome' : 'is-chatting'}`}>
      <div className="chat-window" ref={chatWindowRef} onScroll={handleChatScroll}>
        {isWelcome ? (
          <WelcomeComponent />
        ) : (
          <div className="messages-container" aria-busy={isLoading}>
            {messages.map((message, index) => (
              message.role === 'user' ? (
                <div key={index} className="message user-message">
                  <div className="message-content">{message.content}</div>
                </div>
              ) : (
                <div key={index} className="message assistant-message">
                  {avatar}
                  <div className="message-body">
                    <div className="message-content">
                      <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
                      {message.hasCitations && message.citations && message.citations.length > 0 && (
                        <div className="citations-container">
                          <h6>Citations</h6>
                          <ul className="citation-list">
                            {(expandedCitations[index] ? message.citations : message.citations.slice(0, 5)).map((citation, citIndex) => (
                              <li key={citIndex}>
                                <a className="citation-link" href={citation.url} target="_blank" rel="noopener noreferrer">
                                  <i className={`bi ${citation.type === 'knowledge_base' ? 'bi-journal-text' : 'bi-globe'}`} aria-hidden="true"></i>
                                  {citation.type === 'web' && citation.id ? (
                                    <span className="citation-id">[{citation.id}]</span>
                                  ) : null}
                                  <span className="citation-title">{citation.filename}</span>
                                </a>
                              </li>
                            ))}
                          </ul>
                          {message.citations.length > 5 && (
                            <button
                              type="button"
                              className="show-all-citations-btn"
                              aria-expanded={Boolean(expandedCitations[index])}
                              onClick={() => setExpandedCitations(prev => ({
                                ...prev,
                                [index]: !prev[index]
                              }))}
                            >
                              {expandedCitations[index]
                                ? 'Show less'
                                : `Show all (${message.citations.length - 5} more)`
                              }
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="message-actions">
                      <Button className="export-btn" onClick={() => handleCopyMessage(message)}>
                        <i className="bi bi-clipboard" aria-hidden="true"></i>
                        Copy
                      </Button>
                      <Dropdown>
                        <Dropdown.Toggle
                          className="export-btn"
                          disabled={exportLoading}
                          id={`export-dropdown-${index}`}
                        >
                          <i className="bi bi-file-earmark" aria-hidden="true"></i>
                          {exportLoading ? 'Exporting...' : 'Export'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleExportMessage(message, index, 'md')}
                            disabled={exportLoading}
                          >
                            <i className="bi bi-file-earmark-text me-2"></i>
                            Markdown Document
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleExportMessage(message, index, 'docx')}
                            disabled={exportLoading}
                          >
                            <i className="bi bi-file-earmark-word me-2"></i>
                            Word Document
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              )
            ))}
            {isLoading && (
              <div className="message assistant-message">
                {avatar}
                <div className="message-body">
                  {isStreaming && streamingText ? (
                    <div className="message-content">
                      <ReactMarkdown components={markdownComponents}>{streamingText}</ReactMarkdown>
                      <span className="streaming-cursor" aria-hidden="true">▌</span>
                    </div>
                  ) : activeToolCall ? (
                    <div className="message-content is-status tool-call-indicator" role="status">
                      <i className={`bi ${toolStatus.icon}`} aria-hidden="true"></i>
                      <span>{toolStatus.label}</span>
                    </div>
                  ) : (
                    <div className="message-content is-status typing-indicator" role="status">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span className="visually-hidden">Karl B is thinking</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="input-row">
        <form className="composer" onSubmit={handleSubmit}>
          <textarea
            ref={inputRef}
            className="composer-input"
            rows={1}
            aria-label="Message Karl B"
            placeholder={isWelcome ? 'Ask Karl B about music and arts advocacy...' : 'Reply to Karl B...'}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={MAX_MESSAGE_LENGTH}
          />
          <div className="composer-footer">
            {isNearLimit && (
              <span className="character-counter" aria-live="polite">
                {inputMessage.length} / {MAX_MESSAGE_LENGTH}
              </span>
            )}
            <button
              type="submit"
              className="composer-send"
              aria-label="Send message"
              disabled={!canSend}
            >
              <i className="bi bi-arrow-up" aria-hidden="true"></i>
            </button>
          </div>
        </form>
      </div>

      {isWelcome && (
        <ConversationStarters starters={chatStarters} onSelect={handleStarterClick} />
      )}

      {/* Toast notification */}
      <ToastContainer position="bottom-center" className="p-3">
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={3000} 
          autohide
          bg="light"
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ChatInterface;
